/**
 * Statistical Load Recommendation System
 *
 * Simple, interpretable, works with small datasets.
 * This runs BEFORE TensorFlow ML and handles the 80% case.
 *
 * Philosophy:
 * - Linear regression beats neural networks with <500 data points
 * - Interpretable > complex (users trust what they understand)
 * - RPE 7-8 is the "goldilocks zone" - not too hard, not too easy
 *
 * Priority chain:
 * 1. Trend from successful sets (best predictor)
 * 2. RPE-adjusted last session (good fallback)
 * 3. Program weight (baseline)
 */

import { getSessionHistory, type SessionLog, type PTSessionLog } from './sessionStorage';
import { computeSetVolume } from './volumeTracking';
import { roundToNearestPlate } from './plateMath';
import { resolveExerciseName, normalizeExerciseMatchKey, isAssistedExercise } from './exerciseDatabase';
// The Helms/RTS reps x RPE -> %1RM chart. It already shipped and was already
// used by the demonstrated-capacity ceiling in smartWorkoutGenerator — but the
// recommender that ceiling guards never imported it, so the safety rail was
// strictly smarter than the thing it was protecting.
import { percentForRepsRPE } from './oneRM';

// ============================================================================
// TYPES
// ============================================================================

export interface StatisticalRecommendation {
  weight: number;
  reps: number;
  confidence: number;  // 0-1, how confident we are in this recommendation
  method: 'trend' | 'rpe-adjusted' | 'recent-average' | 'program-weight';
  reasoning: string;   // Human-readable explanation
  dataPoints: number;  // How many data points informed this decision
  trend?: {
    direction: 'increasing' | 'stable' | 'decreasing';
    ratePerSession: number;  // lbs per session
    r2: number;  // How well the trend fits (0-1)
  };
}

export interface ExerciseDataPoint {
  date: Date;
  weight: number;
  reps: number;
  rpe: number | null;
  volume: number;  // weight × reps
  wasSuccessful: boolean;  // Hit target reps with good RPE
  /**
   * Seconds actually held, for timed sets. Carried separately rather than
   * folded into `reps` because a WEIGHTED hold has both a real load and a
   * duration — writing seconds into the reps slot would corrupt the weight
   * trend for exactly those exercises. lib/timedHoldProgression.ts reads this.
   */
  durationSec?: number;
}

// ============================================================================
// MAIN FUNCTION
// ============================================================================

/**
 * Public entry point. Delegates to the core engine, then applies the
 * ASSISTED-EXERCISE INVERSION as a pure post-process. For machine/band
 * assisted movements the logged "weight" is ASSISTANCE (higher = easier), so
 * the core engine's "heavier = progression" direction is backwards. We mirror
 * the recommended weight around the last-known assistance — turning "add load"
 * into "reduce assistance" and vice-versa — WITHOUT touching the core goldilocks
 * logic. Careful by construction: only assisted exercises are transformed, and
 * only when there's a real last-known assistance to pivot on.
 */
export async function getStatisticalRecommendation(
  exerciseName: string,
  targetReps: number,
  periodizationWeight: number,
  readinessMultiplier: number = 1.0
): Promise<StatisticalRecommendation> {
  const raw = await getStatisticalRecommendationCore(exerciseName, targetReps, periodizationWeight, readinessMultiplier);
  if (!isAssistedExercise(exerciseName)) return raw;

  // Weight prescription needs LOADED sets. getExerciseHistory now admits
  // unweighted work too (bodyweight reps, timed holds) because
  // lib/timedHoldProgression.ts reads its duration history through it — but
  // a push-up must stay invisible to the engine that picks a barbell load,
  // so filter here rather than at the source.
  const history = (await getExerciseHistory(exerciseName)).filter(h => h.weight > 0);
  if (history.length === 0) return raw;          // nothing to pivot on
  const lastWeight = history[history.length - 1].weight;
  if (lastWeight <= 0) return raw;

  // Mirror around last assistance: engine "heavier" (its notion of harder) →
  // LESS assistance (actually harder); engine "lighter" → MORE assistance.
  const mirrored = Math.max(0, roundToNearestPlate(2 * lastWeight - raw.weight));
  const dir = mirrored < lastWeight
    ? `reducing assistance ${lastWeight}→${mirrored} lb (getting stronger)`
    : mirrored > lastWeight
      ? `adding assistance ${lastWeight}→${mirrored} lb (bank recovery)`
      : `holding ${mirrored} lb assistance`;
  return {
    ...raw,
    weight: mirrored,
    reasoning: `Assisted movement — less help is harder. Direction inverted: ${dir}.`,
  };
}

/**
 * Get a statistically-grounded weight recommendation
 *
 * This is the PRIMARY recommendation system - simple, interpretable, reliable.
 * TensorFlow ML runs as a secondary "bonus" layer for users with lots of data.
 */
async function getStatisticalRecommendationCore(
  exerciseName: string,
  targetReps: number,
  periodizationWeight: number,
  readinessMultiplier: number = 1.0
): Promise<StatisticalRecommendation> {

  // Get exercise history
  // Weight prescription needs LOADED sets. getExerciseHistory now admits
  // unweighted work too (bodyweight reps, timed holds) because
  // lib/timedHoldProgression.ts reads its duration history through it — but
  // a push-up must stay invisible to the engine that picks a barbell load,
  // so filter here rather than at the source.
  const history = (await getExerciseHistory(exerciseName)).filter(h => h.weight > 0);

  console.log(` Statistical: "${exerciseName}" | ${history.length} data points | Target: ${targetReps} reps @ ${periodizationWeight} lbs`);

  // ============================================================================
  // CASE 1: Not enough data - use program weight OR last-known weight
  // ============================================================================
  if (history.length < 3) {
    // If we have at least 1 data point, use last-known weight instead of potentially-zero program weight
    if (history.length > 0 && (periodizationWeight === 0 || history[history.length - 1].weight > 0)) {
      const lastWeight = history[history.length - 1].weight;
      const adjustedWeight = roundToNearestPlate(lastWeight * readinessMultiplier);

      console.log(` Using last-known weight: ${lastWeight} lbs (history: ${history.length} sessions, program: ${periodizationWeight})`);

      return {
        weight: adjustedWeight,
        reps: targetReps,
        confidence: 0.55, // Must be >= 0.5 to be used by smartWorkoutGenerator
        method: 'recent-average',
        reasoning: `Using last session weight: ${lastWeight} lbs (limited history)`,
        dataPoints: history.length,
      };
    }

    const adjustedWeight = roundToNearestPlate(periodizationWeight * readinessMultiplier);
    return {
      weight: adjustedWeight,
      reps: targetReps,
      confidence: 0.3,
      method: 'program-weight',
      reasoning: `First time or limited history (${history.length} sessions) - using program weight`,
      dataPoints: history.length,
    };
  }

  // ============================================================================
  // CASE 2: Find "goldilocks" sets - successful reps with RPE 7-8
  // ============================================================================
  const goldilocksSets = history.filter(h =>
    h.reps >= targetReps &&
    h.rpe !== null &&
    h.rpe >= 6.5 &&
    h.rpe <= 8.5 &&
    h.wasSuccessful
  );

  // Also find sets at similar rep ranges (±2 reps)
  const similarRepSets = history.filter(h =>
    Math.abs(h.reps - targetReps) <= 2 &&
    h.rpe !== null &&
    h.rpe >= 6 &&
    h.rpe <= 9
  );

  // ============================================================================
  // APPROACH 1: Trend-based recommendation (best with enough data)
  // ============================================================================
  if (goldilocksSets.length >= 4) {
    /**
     * ONE POINT PER SESSION, not per set.
     *
     * The window used to be the last 6 SETS, which on a 3x8 day is two
     * sessions. Six sets from one session are six near-identical numbers, so
     * the regression saw a flat line where there was no information, and a
     * lifter doing more sets per session got a shorter effective memory.
     * Aggregate to the top working set of each session — that is the datum
     * that represents the day.
     */
    const bySession = new Map<string, ExerciseDataPoint>();
    for (const s of goldilocksSets) {
      const key = s.date.toISOString().slice(0, 10);
      const best = bySession.get(key);
      if (!best || s.weight > best.weight) bySession.set(key, s);
    }
    const sessionPoints = Array.from(bySession.values())
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .slice(-6);

    /**
     * REP-NORMALISED CURRENCY.
     *
     * Regressing raw pounds treats 250x10 and 250x6 as the same observation,
     * so changing rep scheme reads as changing strength. Convert each session
     * to a load-equivalent at TODAY's target reps before fitting, using the
     * same Helms/RTS chart the demonstrated-capacity ceiling already uses
     * (smartWorkoutGenerator.ts:1941). Measured on real data, RIR-adjusted
     * normalisation cuts within-session dispersion from 4.24% to ~3.0%;
     * rep-only e1RM does not (it is slightly worse than raw).
     *
     * Assisted movements are excluded upstream (see isAssistedExercise) — on
     * an inverted scale this conversion would be backwards.
     */
    const equivalentAtTarget = (s: ExerciseDataPoint): number => {
      const rpe = s.rpe ?? 7.5; // ratio is insensitive to the default
      const from = percentForRepsRPE(s.reps, rpe);
      const to = percentForRepsRPE(targetReps, rpe);
      if (!from || !to) return s.weight;
      return s.weight * (to / from);
    };
    const equivalents = sessionPoints.map(equivalentAtTarget);
    const trend = linearRegression(equivalents);

    /**
     * ANCHORED PROJECTION — the fix for the 2026-08-10 over-prescription.
     *
     * The old line was `slope * weights.length + intercept`, i.e. evaluate the
     * fit at index N when the observations occupy 0..N-1. That extrapolates a
     * full session BEYOND everything ever observed, every single time. With a
     * slope inflated by mixed rep ranges it compounds: the RDL was projected
     * to 255x8 when 245 for 6 was already a limit set.
     *
     * Now: evaluate the fit at the LAST observed index (no free extrapolation),
     * and separately cap the session-over-session increase. A lifter can still
     * progress — just not faster than a plausible weekly increment.
     */
    const lastIdx = equivalents.length - 1;
    const fitted = trend.slope * lastIdx + trend.intercept;
    const lastEquivalent = equivalents[lastIdx];

    /**
     * ROBUST ANCHOR — one light day must not reset the whole lift.
     *
     * Both the floor and the ceiling below used to hang off `lastEquivalent`,
     * the single most recent session. That makes the anchor as volatile as the
     * noisiest entry in the history, and the clamp written to stop "a bad fit
     * tanking it" was itself pinned to the outlier it needed protection from.
     *
     * Measured on ecorbin791's real Bench Press history: 2026-01-27 logs
     * `85x10 @RPE8` four times — one light session under a name that
     * otherwise runs 185-225. It is RPE 8 and successful, so it passes the
     * goldilocks filter and becomes `lastEquivalent`. The 0.9 floor then sat
     * near 76 lb instead of 190, and the engine prescribed 95 lb for a lift
     * the athlete took to 205x8 that day — a 54% under-prescription reported
     * at 0.93 confidence.
     *
     * The fix is OUTLIER REJECTION, not a central tendency. Measured both:
     * replacing the anchor with the median of the last three sessions fixed
     * the Bench case but left the aggregate flat and nudged the within-10%
     * rate DOWN (50.5% -> 49.5%). Of course it did — for a lifter who is
     * actually progressing, the most recent session IS the best anchor, and a
     * median systematically lags it.
     *
     * So: keep `lastEquivalent`, and only distrust it when it is far below the
     * recent distribution. A genuine deload or detraining block moves several
     * sessions and drags the median with it, so it still lowers the anchor.
     * One anomalous log sits >15% under the median and gets ignored.
     *
     * Deliberately NOT the max: ratcheting to the best-ever session is how an
     * engine stops being able to prescribe a reduction at all, which is the
     * defect this file already carries a comment about further down.
     */
    const recentWindow = [...equivalents.slice(-6)].sort((a, b) => a - b);
    const recentMedian = recentWindow.length
      ? recentWindow[Math.floor(recentWindow.length / 2)]
      : lastEquivalent;
    /**
     * Swept 0.70-0.95 against the real export. The metric is FLAT:
     *   0.70  MAE 16.6  within10 50.8%      0.85  MAE 16.5  within10 51.6%
     *   0.75  MAE 16.5  within10 51.4%      0.90  MAE 16.4  within10 52.2%
     *   0.80  MAE 16.5  within10 51.4%      0.95  MAE 16.4  within10 52.2%
     * At n=321 the whole spread is about two predictions.
     *
     * 0.95 scores marginally best and is the WRONG choice. At 0.95 the anchor
     * is essentially never allowed below the recent median, which is
     * ratcheting by another name — and the backtest rewards ratcheting
     * structurally, because it scores against the athlete's TOP set and the
     * top set is at or above the median by construction. That is also why the
     * naive "repeat your proven best" baseline wins: it IS the ratchet. An
     * engine tuned to this metric would converge on never prescribing a
     * reduction, which is the defect this file already fights elsewhere.
     *
     * 0.85 is the principled midpoint: comfortably outside normal
     * session-to-session variation, still able to follow a real deload down.
     */
    const OUTLIER_FLOOR = 0.85;
    const anchor = recentWindow.length >= 3 && lastEquivalent < recentMedian * OUTLIER_FLOOR
      ? recentMedian
      : lastEquivalent;

    // Cap: never more than ~3% above the demonstrated anchor. That is roughly
    // one plate jump on a 250 lb lift — real progress, not a leap.
    const MAX_SESSION_INCREASE = 1.03;
    const capped = Math.min(
      Math.max(fitted, anchor * 0.9), // don't let a bad fit tank it either
      anchor * MAX_SESSION_INCREASE,
    );

    /**
     * HARD FAILURE GATE. If the most recent session was a miss or a grind,
     * do not increase at all — hold or back off. Under the old code a missed
     * prescription still fed an upward trend, which is exactly how a bad day
     * became a heavier next day.
     */
    /**
     * THE GATE WAS DEAD BY CONSTRUCTION.
     *
     * It used to read `sessionPoints[lastIdx]` — derived from `goldilocksSets`,
     * which is filtered to `rpe >= 6.5 && rpe <= 8.5 && wasSuccessful`. Both
     * halves of the test (`rpe >= 9`, `wasSuccessful === false`) are therefore
     * impossible on that data: the filter removes exactly the sets the gate
     * exists to detect. It could never fire, so a failed or grinding session
     * still fed the upward trend — which is precisely the failure the comment
     * above claims to prevent.
     *
     * Judge the grind against RAW history instead: the most recent session the
     * athlete actually logged, whatever its quality. That is the session whose
     * difficulty should decide whether today goes up.
     */
    const lastLoggedDay = history.length > 0
      ? history[history.length - 1].date.toISOString().slice(0, 10)
      : null;
    const lastLoggedSets = lastLoggedDay
      ? history.filter(h => h.date.toISOString().slice(0, 10) === lastLoggedDay)
      : [];
    const lastWasGrind = lastLoggedSets.some(
      h => (h.rpe ?? 0) >= 9 || h.wasSuccessful === false,
    );
    // Clamped to the robust anchor, not to `lastEquivalent`. The intent is "a
    // hard day earns no increase", and the athlete's established working level
    // expresses that better than whatever the single last entry happened to
    // be — otherwise one light log re-opens the outlier hole above every time
    // the previous session also happened to be a grind.
    const gated = lastWasGrind ? Math.min(capped, anchor) : capped;

    const adjustedProjected = gated * readinessMultiplier;
    const finalWeight = roundToNearestPlate(adjustedProjected);

    // Determine trend direction
    let direction: 'increasing' | 'stable' | 'decreasing' = 'stable';
    if (trend.slope > 1) direction = 'increasing';
    else if (trend.slope < -1) direction = 'decreasing';

    /**
     * CONFIDENCE MUST BE ABOUT FIT, NOT ABOUT VOLUME.
     *
     * The old formula was `min(0.9, 0.5 + n * 0.05) + r2 * 0.1`. It saturates
     * at EIGHT sets, so past that point confidence was a constant 0.9 plus a
     * rounding error of fit — quantity was worth 0.40 of the range and quality
     * 0.10.
     *
     * Measured over 336 real walk-forward predictions:
     *     Spearman(confidence, |% error|) = -0.018
     *     mean |err| by band: <0.5 n=0 | 0.50-0.69 14.0% | 0.70-0.84 14.6%
     *                         | >=0.85 14.5%
     * Flat. The top band held 164 of 336 predictions and was no more accurate
     * than the bottom. Confidence was not inverted (as the audit speculated
     * and correctly declined to conclude at n=43) — it was inert, and it was
     * printed next to every prescription as if it meant something.
     *
     * It also made the two downstream `confidence >= 0.5` gates
     * (smartWorkoutGenerator P1, unifiedRecommendation) unreachable: nothing
     * ever scored below 0.5, so neither gate rejected a single prescription.
     *
     * REWEIGHTING TOWARD r2 WAS TRIED AND REJECTED — do not redo it.
     *
     * `0.20 + min(0.25, n*0.03) + r2*0.45` was measured on the same 336 rows:
     *     Spearman  -0.018  ->  -0.052        (still nil)
     *     mean |err| by band  14.4% | 11.9% | 15.9% | 17.5%   (NOT monotonic)
     * The MAE-by-band ordering did improve, but only because heavier lifts
     * carry larger absolute errors — the percentage view, which is the
     * scale-free one, got worse at high confidence.
     *
     * So in-sample fit quality does not predict out-of-sample prescription
     * error here either. Shipping the reweight would have pushed 74 of 336
     * predictions below the two `>= 0.5` gates on the strength of a signal
     * with no demonstrated value: a real behaviour change bought with nothing.
     * Formula therefore left as it was.
     *
     * The genuine fix is audit item 4.4 — route lib/loadDeviation.ts's
     * REALIZED error into this number, so confidence is earned by past
     * accuracy rather than asserted from sample size. Until then, treat the
     * confidence shown beside a prescription as decoration, and consider
     * removing it from the UI rather than continuing to display a number that
     * is measurably uninformative.
     */
    const baseConfidence = Math.min(0.9, 0.5 + goldilocksSets.length * 0.05);
    const fitBonus = trend.r2 * 0.1;  // Better fit = more confidence
    const confidence = Math.min(0.95, baseConfidence + fitBonus);

    const trendText = trend.slope > 0
      ? `+${trend.slope.toFixed(1)} lbs/session`
      : `${trend.slope.toFixed(1)} lbs/session`;

    console.log(` Trend: ${trendText} (R²=${trend.r2.toFixed(2)}) from ${goldilocksSets.length} successful sets`);

    return {
      weight: finalWeight,
      reps: targetReps,
      confidence,
      method: 'trend',
      reasoning: `Trend from ${goldilocksSets.length} successful sets: ${trendText}`,
      dataPoints: goldilocksSets.length,
      trend: {
        direction,
        ratePerSession: trend.slope,
        r2: trend.r2,
      },
    };
  }

  // ============================================================================
  // APPROACH 2: RPE-adjusted last session
  // ============================================================================
  const recentSets = similarRepSets.length > 0 ? similarRepSets : history;
  const lastSession = recentSets[recentSets.length - 1];

  if (lastSession && lastSession.rpe !== null) {
    // RPE 8 = perfect target
    // Below 8 = could have done more → increase weight
    // Above 8 = was too hard → decrease weight
    // Adjustment: 2.5% per RPE point difference
    const rpeDiff = 8 - lastSession.rpe;
    const rpeAdjustment = rpeDiff * 0.025;

    // Also consider rep completion
    let repAdjustment = 0;
    if (lastSession.reps > targetReps) {
      // Did more reps than needed → could go heavier
      repAdjustment = 0.02;
    } else if (lastSession.reps < targetReps) {
      // Didn't hit target reps → might need to go lighter
      repAdjustment = -0.03;
    }

    const totalAdjustment = rpeAdjustment + repAdjustment;
    const adjusted = lastSession.weight * (1 + totalAdjustment) * readinessMultiplier;
    const finalWeight = roundToNearestPlate(adjusted);

    // Confidence based on how recent and how close to target reps
    const recency = recentSets.length >= 3 ? 0.7 : 0.55;
    const repMatch = lastSession.reps === targetReps ? 0.1 : 0;
    const confidence = Math.min(0.8, recency + repMatch);

    const adjustmentText = totalAdjustment > 0
      ? `+${(totalAdjustment * 100).toFixed(0)}%`
      : `${(totalAdjustment * 100).toFixed(0)}%`;

    console.log(` RPE-adjusted: Last session ${lastSession.weight} lbs @ RPE ${lastSession.rpe} → ${adjustmentText}`);

    return {
      weight: finalWeight,
      reps: targetReps,
      confidence,
      method: 'rpe-adjusted',
      reasoning: `Last session: ${lastSession.weight} lbs @ RPE ${lastSession.rpe} → ${adjustmentText} adjustment`,
      dataPoints: recentSets.length,
    };
  }

  // ============================================================================
  // APPROACH 3: Recent average (fallback)
  // ============================================================================
  if (history.length >= 3) {
    const recentWeights = history.slice(-5).map(h => h.weight);
    const avgWeight = recentWeights.reduce((a, b) => a + b, 0) / recentWeights.length;
    const adjusted = avgWeight * readinessMultiplier;
    const finalWeight = roundToNearestPlate(adjusted);

    console.log(` Average: ${avgWeight.toFixed(0)} lbs from last ${recentWeights.length} sessions`);

    return {
      weight: finalWeight,
      reps: targetReps,
      confidence: 0.5,
      method: 'recent-average',
      reasoning: `Average of last ${recentWeights.length} sessions: ${avgWeight.toFixed(0)} lbs`,
      dataPoints: recentWeights.length,
    };
  }

  // ============================================================================
  // FALLBACK: Program weight OR last-known weight if program is 0
  // ============================================================================

  // If program weight is 0 but we have history, use last-known weight
  if (periodizationWeight === 0 && history.length > 0) {
    const lastWeight = history[history.length - 1].weight;
    const adjustedWeight = roundToNearestPlate(lastWeight * readinessMultiplier);

    console.log(` Fallback to last-known: ${lastWeight} lbs (program weight was 0)`);

    return {
      weight: adjustedWeight,
      reps: targetReps,
      confidence: 0.55, // Must be >= 0.5 to be used by smartWorkoutGenerator
      method: 'recent-average',
      reasoning: `Using last session weight: ${lastWeight} lbs (program defines no target)`,
      dataPoints: history.length,
    };
  }

  const adjustedWeight = roundToNearestPlate(periodizationWeight * readinessMultiplier);

  return {
    weight: adjustedWeight,
    reps: targetReps,
    confidence: 0.4,
    method: 'program-weight',
    reasoning: 'Using periodization weight from program',
    dataPoints: history.length,
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract exercise-specific history from all sessions
 * Uses alias resolution to match exercise names across variations
 */
export async function getExerciseHistory(exerciseName: string): Promise<ExerciseDataPoint[]> {
  const sessions = await getSessionHistory();
  const dataPoints: ExerciseDataPoint[] = [];

  // Variant-tolerant match key — collapses spelling variants of the SAME lift
  // (DB↔Dumbbell, hyphens, plural/singular, Tricep↔Triceps) so fragmented
  // history reunites, WITHOUT merging different lifts (incline/flat, barbell/
  // dumbbell, seated/standing stay distinct). Replaces both the old over-eager
  // substring `includes` (which swallowed Incline into Bench) AND the too-tight
  // exact equality (which split "Incline DB Press" from "Incline Dumbbell
  // Press"). Real data had Incline DB logged under 3 names → near-zero history.
  const targetKey = normalizeExerciseMatchKey(exerciseName);

  for (const session of sessions) {
    if (session.sessionType !== 'pt' || !('exercises' in session)) continue;

    const ptSession = session as PTSessionLog;

    for (const exercise of ptSession.exercises) {
      const isMatch = normalizeExerciseMatchKey(exercise.exerciseName) === targetKey;

      if (isMatch) {
        // Extract data from each set
        if (exercise.sets && exercise.sets.length > 0) {
          for (const set of exercise.sets) {
            // Skip warmup sets — light ramp loads drag the trend/average down
            // toward warmup weight, producing absurdly low recommendations.
            if (set.kind === 'warmup') continue;

            /**
             * A set only counts as EVIDENCE if it was actually performed.
             *
             * This used to admit any row with weight>0 and reps>0, and then
             * scored it `wasSuccessful: set.completed !== false` — so a set
             * with `completed` UNDEFINED (never touched, seeded by the
             * generator and left alone) entered the history as a SUCCESS at
             * its prescribed weight. The recommender was reading its own
             * prescriptions back as though the user had hit them, which is a
             * feedback loop that confirms whatever it already believed.
             */
            if (set.completed !== true) continue;

            /**
             * `weight > 0` used to be required, which silently excluded every
             * UNWEIGHTED set from history: bodyweight work and timed holds.
             * Measured against production: of 165 timed logs, zero sets pass
             * the old gate, so lib/timedHoldProgression.ts — which reads its
             * history through here — returned `insufficient-data` on 165/165
             * and could never move a plank.
             *
             * A set is evidence when it was PERFORMED, which means reps (or,
             * on a hold, seconds in the reps slot) greater than zero. Weight
             * is a property of the set, not a precondition for it existing.
             * Consumers that need a load already filter on `weight > 0`
             * themselves; the weight-trend paths ignore zero-weight points.
             */
            if (set.reps && set.reps > 0) {
              /**
               * Success = performed AND met the prescription.
               *
               * `completed !== false` made every performed set a success even
               * when the user missed reps badly. On 2026-08-10 the RDL was
               * prescribed 255x8 and the user managed 245 for 6/5/5 — under
               * the old rule all three sets were "successful" and fed the
               * upward trend that produced the over-prescription in the first
               * place. Misses must be able to vote.
               *
               * When no prescription was recorded (user-added exercises,
               * pre-telemetry history) we cannot judge attainment, so a
               * performed set still counts — otherwise older history vanishes.
               */
              const metPrescription =
                set.recommendedReps == null || set.reps >= set.recommendedReps;

              dataPoints.push({
                date: new Date(session.date),
                weight: set.weight,
                reps: set.reps,
                rpe: set.rpe ?? null,
                volume: computeSetVolume(set, exercise.exerciseName),
                wasSuccessful: metPrescription,
                ...(typeof set.durationSec === 'number' && set.durationSec > 0
                  ? { durationSec: set.durationSec }
                  : {}),
              });
            }
          }
        }

        /**
         * CIRCUIT ROUNDS.
         *
         * A circuit block writes to `circuitSets`, not `sets` — a completely
         * separate schema (round / time / rpe / weight / distance). Every
         * engine here reads `sets`, so a movement performed inside a circuit
         * has always contributed ZERO history: it neither progressed nor
         * informed. For a carry that is the whole ballgame, because distance
         * is its progression lever and a circuit-formatted carry could never
         * accumulate the history needed to move it.
         *
         * Deliberately narrow: a round only becomes a data point when it has
         * BOTH a load and a distance. That admits exactly the loaded-carry
         * case (where metres are the rep-analog, matching how `reps` already
         * stores metres on a carry set) and naturally excludes everything
         * else — an ordinary circuit movement records no distance, so it
         * cannot leak into a strength lift's weight trend. Metabolic circuit
         * work has no business voting on how heavy a squat should be.
         */
        if (exercise.circuitSets && exercise.circuitSets.length > 0) {
          for (const round of exercise.circuitSets) {
            if (!round.completed) continue;
            const load = round.weight ?? 0;
            const metres = round.distance ?? 0;
            if (load <= 0 || metres <= 0) continue;

            dataPoints.push({
              date: new Date(session.date),
              weight: load,
              reps: metres, // metres — same convention as a carry's `sets[].reps`
              rpe: round.rpe ?? null,
              volume: load * metres,
              // Circuits record no per-round prescription to compare against,
              // so a completed round counts as performed-and-met. The carry
              // engine judges effort from RPE and completion separately.
              wasSuccessful: true,
            });
          }
        }
      }
    }
  }

  // Sort by date
  dataPoints.sort((a, b) => a.date.getTime() - b.date.getTime());

  return dataPoints;
}

/**
 * Simple linear regression
 * Returns slope, intercept, and R² (goodness of fit)
 */
function linearRegression(y: number[]): { slope: number; intercept: number; r2: number } {
  const n = y.length;
  if (n < 2) return { slope: 0, intercept: y[0] || 0, r2: 0 };

  const x = y.map((_, i) => i);

  const sumX = x.reduce((a, b) => a + b, 0);
  const sumY = y.reduce((a, b) => a + b, 0);
  const sumXY = x.reduce((total, xi, i) => total + xi * y[i], 0);
  const sumX2 = x.reduce((total, xi) => total + xi * xi, 0);
  const sumY2 = y.reduce((total, yi) => total + yi * yi, 0);

  const denominator = n * sumX2 - sumX * sumX;
  if (denominator === 0) return { slope: 0, intercept: y[n - 1], r2: 0 };

  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;

  // Calculate R² (coefficient of determination)
  const yMean = sumY / n;
  const ssTotal = y.reduce((total, yi) => total + Math.pow(yi - yMean, 2), 0);
  const ssResidual = y.reduce((total, yi, i) => {
    const predicted = slope * i + intercept;
    return total + Math.pow(yi - predicted, 2);
  }, 0);

  const r2 = ssTotal > 0 ? 1 - (ssResidual / ssTotal) : 0;

  return {
    slope: isFinite(slope) ? slope : 0,
    intercept: isFinite(intercept) ? intercept : y[n - 1],
    r2: Math.max(0, Math.min(1, r2)),
  };
}

/**
 * Get a summary of exercise history for debugging/display
 */
export async function getExerciseStatsSummary(exerciseName: string): Promise<{
  totalSets: number;
  avgWeight: number;
  maxWeight: number;
  avgRPE: number | null;
  trend: 'increasing' | 'stable' | 'decreasing';
  lastSessionDate: Date | null;
}> {
  // Weight prescription needs LOADED sets. getExerciseHistory now admits
  // unweighted work too (bodyweight reps, timed holds) because
  // lib/timedHoldProgression.ts reads its duration history through it — but
  // a push-up must stay invisible to the engine that picks a barbell load,
  // so filter here rather than at the source.
  const history = (await getExerciseHistory(exerciseName)).filter(h => h.weight > 0);

  if (history.length === 0) {
    return {
      totalSets: 0,
      avgWeight: 0,
      maxWeight: 0,
      avgRPE: null,
      trend: 'stable',
      lastSessionDate: null,
    };
  }

  const weights = history.map(h => h.weight);
  const avgWeight = weights.reduce((a, b) => a + b, 0) / weights.length;
  const maxWeight = Math.max(...weights);

  const rpeSets = history.filter(h => h.rpe !== null);
  const avgRPE = rpeSets.length > 0
    ? rpeSets.reduce((sum, h) => sum + (h.rpe || 0), 0) / rpeSets.length
    : null;

  // Calculate trend from recent data
  const recentWeights = history.slice(-10).map(h => h.weight);
  const { slope } = linearRegression(recentWeights);

  let trend: 'increasing' | 'stable' | 'decreasing' = 'stable';
  if (slope > 1) trend = 'increasing';
  else if (slope < -1) trend = 'decreasing';

  return {
    totalSets: history.length,
    avgWeight: Math.round(avgWeight),
    maxWeight,
    avgRPE: avgRPE !== null ? Math.round(avgRPE * 10) / 10 : null,
    trend,
    lastSessionDate: history[history.length - 1].date,
  };
}
